import { apiClient } from './ApiClient';

export interface QuickBooksConnection {
  id: number;
  user_id: string;
  realm_id: string;
  company_name: string;
  status: string;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
  company_info?: {
    CompanyName: string;
    LegalName?: string;
    CompanyAddr?: {
      Line1?: string;
      City?: string;
      CountrySubDivisionCode?: string;
      PostalCode?: string;
    };
  };
}

export interface QuickBooksAccount {
  id: string;
  connection_id: number;
  name: string;
  account_type: string;
  account_sub_type: string;
  balance: number;
  currency: string;
  active: boolean;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuthUrlResponse {
  auth_url: string;
  state: string;
}

export interface ConnectQuickBooksRequest {
  authCode: string;
  realmId: string;
}

export interface ConnectQuickBooksResponse {
  connection: QuickBooksConnection;
  message: string;
}

export interface SyncAccountsResponse {
  accounts: QuickBooksAccount[];
  count: number;
  message: string;
}

export interface QuickBooksTransaction {
  id: string;
  connection_id: number;
  quickbooks_account_id: string;
  transaction_type: string;
  transaction_date: string;
  amount: number;
  currency: string;
  description: string | null;
  memo: string | null;
  line_items: any;
  sync_status: string;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SyncTransactionsResponse {
  transactions: QuickBooksTransaction[];
  count: number;
  message: string;
}

export interface MapTransactionRequest {
  assetId: string;
  category?: string;
  unit?: number | null;
}

export interface MapTransactionResponse {
  transaction: any;
  message: string;
}

class QuickBooksService {
  /**
   * Get authorization URL to start OAuth flow
   */
  async getAuthUrl(): Promise<AuthUrlResponse> {
    const response = await apiClient.get<AuthUrlResponse>('/quickbooks/auth-url');
    return response.data;
  }

  /**
   * Connect QuickBooks after OAuth callback
   */
  async connectQuickBooks(
    data: ConnectQuickBooksRequest,
  ): Promise<ConnectQuickBooksResponse> {
    const response = await apiClient.post<ConnectQuickBooksResponse>(
      '/quickbooks/connect',
      data,
    );
    return response.data;
  }

  /**
   * Get all QuickBooks connections for the current user
   */
  async getConnections(): Promise<QuickBooksConnection[]> {
    const response = await apiClient.get<QuickBooksConnection[]>(
      '/quickbooks/connections',
    );
    return response.data;
  }

  /**
   * Get a specific QuickBooks connection
   */
  async getConnection(connectionId: number): Promise<QuickBooksConnection> {
    const response = await apiClient.get<QuickBooksConnection>(
      `/quickbooks/connections/${connectionId}`,
    );
    return response.data;
  }

  /**
   * Sync accounts from QuickBooks
   */
  async syncAccounts(connectionId: number): Promise<SyncAccountsResponse> {
    const response = await apiClient.post<SyncAccountsResponse>(
      `/quickbooks/connections/${connectionId}/sync-accounts`,
    );
    return response.data;
  }

  /**
   * Get synced accounts for a connection
   */
  async getAccounts(connectionId: number): Promise<QuickBooksAccount[]> {
    const response = await apiClient.get<QuickBooksAccount[]>(
      `/quickbooks/connections/${connectionId}/accounts`,
    );
    return response.data;
  }

  /**
   * Link a QuickBooks connection to an entity
   */
  async linkEntity(connectionId: number, entityId: number): Promise<void> {
    await apiClient.post(`/quickbooks/connections/${connectionId}/link-entity`, {
      entityId,
    });
  }

  /**
   * Sync transactions from QuickBooks
   */
  async syncTransactions(
    connectionId: number,
    options?: {
      from?: string;
      to?: string;
      accountId?: string;
    },
  ): Promise<SyncTransactionsResponse> {
    const params = new URLSearchParams();
    if (options?.from) params.append("from", options.from);
    if (options?.to) params.append("to", options.to);
    if (options?.accountId) params.append("accountId", options.accountId);

    const queryString = params.toString();
    const url = `/quickbooks/connections/${connectionId}/sync-transactions${
      queryString ? `?${queryString}` : ""
    }`;

    const response = await apiClient.post<SyncTransactionsResponse>(url);
    return response.data;
  }

  /**
   * Get synced transactions for a connection
   */
  async getTransactions(connectionId: number): Promise<QuickBooksTransaction[]> {
    const response = await apiClient.get<QuickBooksTransaction[]>(
      `/quickbooks/connections/${connectionId}/transactions`,
    );
    return response.data;
  }

  /**
   * Map a QuickBooks transaction to an asset
   */
  async mapTransactionToAsset(
    connectionId: number,
    transactionId: string,
    data: MapTransactionRequest,
  ): Promise<MapTransactionResponse> {
    const response = await apiClient.post<MapTransactionResponse>(
      `/quickbooks/connections/${connectionId}/transactions/${transactionId}/map`,
      data,
    );
    return response.data;
  }

  /**
   * Disconnect QuickBooks (revoke access)
   */
  async disconnectQuickBooks(connectionId: number): Promise<void> {
    await apiClient.delete(`/quickbooks/connections/${connectionId}`);
  }
}

export default new QuickBooksService();

